import { useQuery } from '@tanstack/preact-query';
import { useContext, useEffect, useState } from 'preact/hooks';
import { ApiClientContext, type PlaceSearchResult } from '../arcticapi';
import {
  findPlaceByQuery,
  formatPlaceOption,
  toPlaceOptions,
  type PlaceOption,
} from './places';
import type { CurrentCoordinates } from './currentLocation';
import { useDebouncedValue } from './useDebouncedValue';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * State container for place autocomplete in journey planning forms.
 */
export interface UsePlaceSearchResult {
  query: string;
  setQuery: (value: string) => void;
  places: PlaceSearchResult[];
  options: PlaceOption[];
  loading: boolean;
  error: Error | null;
  setResolvedPlace: (
    place: PlaceSearchResult | null,
    queryOverride?: string,
  ) => void;
  resolveSelectedPlace: (queryOverride?: string) => PlaceSearchResult | null;
}

interface UsePlaceSearchOptions {
  currentCoordinates?: CurrentCoordinates | null;
}

/**
 * Searches `/places` as the user types and returns both raw matches and
 * datalist-friendly options.
 */
export function usePlaceSearch(
  initialQuery = '',
  options: UsePlaceSearchOptions = {},
): UsePlaceSearchResult {
  const client = useContext(ApiClientContext);
  const [query, setQueryState] = useState(initialQuery);
  const [resolvedPlace, setResolvedPlaceState] =
    useState<PlaceSearchResult | null>(null);
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedQuery.trim();
  const isSearchable = trimmedQuery.length >= 2;
  const near = toLatLngString(options.currentCoordinates);
  const queryKey = near
    ? ['place-search', trimmedQuery, near]
    : ['place-search', trimmedQuery];

  useEffect(() => {
    setQueryState(initialQuery);
    setResolvedPlaceState(null);
  }, [initialQuery]);

  const placesQuery = useQuery<PlaceSearchResult[], Error>({
    queryKey,
    enabled: Boolean(client) && isSearchable,
    queryFn: ({ signal }) => {
      if (!client) {
        throw new Error('Place search is unavailable in this preview context.');
      }

      return client.searchPlaces(
        near
          ? {
              query: trimmedQuery,
              near,
            }
          : trimmedQuery,
        { signal },
      );
    },
  });

  const searchResults = isSearchable ? (placesQuery.data ?? []) : [];
  const setQuery = (value: string) => {
    setQueryState(value);

    if (!resolvedPlace) {
      return;
    }

    const nextSelection = findPlaceByQuery(value, [resolvedPlace]);
    if (!nextSelection) {
      setResolvedPlaceState(null);
    }
  };

  return {
    query,
    setQuery,
    places: searchResults,
    options: toPlaceOptions(searchResults),
    loading: Boolean(client) && isSearchable && placesQuery.isPending,
    error: placesQuery.error,
    setResolvedPlace: (place, queryOverride) => {
      setResolvedPlaceState(place);

      if (typeof queryOverride === 'string') {
        setQueryState(queryOverride);
        return;
      }

      if (place) {
        setQueryState(formatPlaceOption(place));
      }
    },
    resolveSelectedPlace: () =>
      resolvedPlace ?? findPlaceByQuery(query, searchResults),
  };
}

function toLatLngString(
  currentCoordinates: CurrentCoordinates | null | undefined,
): `${number},${number}` | undefined {
  if (!currentCoordinates) {
    return undefined;
  }

  return `${currentCoordinates.latitude},${currentCoordinates.longitude}`;
}
